import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const safeJson = (v) => {
  try {
    if (v == null) return null;
    if (typeof v === 'string') return JSON.parse(v);
    return v;
  } catch {
    return v;
  }
};

const formatValue = (v) => {
  if (v == null) return '';
  if (typeof v === 'object') return JSON.stringify(v, null, 2);
  return String(v);
};

const prettyTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

const DetailsModal = ({ open, onClose, notification }) => {
  const metadata = useMemo(() => safeJson(notification?.metadata), [notification]);

  if (!open) return null;

  const lead = metadata?.lead || {};
  const plan = metadata?.plan || {};

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* overlay */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* panel */}
      <div className="relative w-full sm:max-w-2xl bg-background rounded-t-2xl sm:rounded-2xl shadow-xl border border-border max-h-[85vh] overflow-hidden">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="FileText" size={18} className="text-primary" />
            <h3 className="font-semibold text-foreground">Detalles de notificación</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition"
            aria-label="Cerrar"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(85vh-64px)]">
          <div className="space-y-4">
            <div className="rounded-xl border border-border p-4">
              <div className="text-sm text-muted-foreground">Título</div>
              <div className="font-semibold text-foreground">{notification?.title || '-'}</div>

              <div className="mt-3 text-sm text-muted-foreground">Mensaje</div>
              <div className="text-foreground">{notification?.message || '-'}</div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-sm text-muted-foreground">Tipo</div>
                  <div className="text-foreground">{notification?.type || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Prioridad</div>
                  <div className="text-foreground">{notification?.priority || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Creado</div>
                  <div className="text-foreground">{prettyTime(notification?.createdAt || notification?.created_at)}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Link (interno)</div>
                  <div className="text-foreground break-all">{notification?.link || '-'}</div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-border p-4">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="User" size={16} className="text-primary" />
                <div className="font-semibold text-foreground">Lead</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-muted-foreground">Nombre</div>
                  <div className="text-foreground">{lead?.nombre || lead?.name || '-'}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Email</div>
                  <div className="text-foreground break-all">{lead?.email || '-'}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Teléfono</div>
                  <div className="text-foreground">{lead?.telefono || lead?.phone || '-'}</div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-border p-4">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Package" size={16} className="text-primary" />
                <div className="font-semibold text-foreground">Plan</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-muted-foreground">Nombre</div>
                  <div className="text-foreground">{plan?.name || '-'}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Precio</div>
                  <div className="text-foreground">{plan?.price || '-'}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Proyecto</div>
                  <div className="text-foreground">{plan?.project || '-'}</div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-border p-4">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Braces" size={16} className="text-primary" />
                <div className="font-semibold text-foreground">Metadata (raw)</div>
              </div>
              <pre className="text-xs bg-muted/40 border border-border rounded-lg p-3 overflow-x-auto">
{formatValue(metadata)}
              </pre>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} iconName="X">
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const NotificationFeed = ({ notifications = [], onMarkAsRead, onMarkAllAsRead }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const openDetails = (n) => {
    setSelected(n);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelected(null);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Bell" size={18} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Notifications</h2>
        </div>
        <Button variant="outline" onClick={onMarkAllAsRead} iconName="Check">
          Mark all read
        </Button>
      </div>

      <div className="space-y-3">
        {(notifications || []).map((n) => (
          <div
            key={n?.id}
            className={`rounded-xl border p-4 transition ${
              n?.read ? 'border-border bg-background' : 'border-primary/40 bg-primary/5'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-foreground truncate">
                  {n?.title || 'Notificación'}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {n?.message || ''}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {!n?.read && (
                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                )}
                <button
                  className="text-primary text-sm hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openDetails(n);
                    if (!n?.read) onMarkAsRead?.(n?.id);
                  }}
                >
                  View details
                </button>
              </div>
            </div>
          </div>
        ))}

        {(!notifications || notifications.length === 0) && (
          <div className="text-sm text-muted-foreground py-6 text-center">
            Aún no hay notificaciones.
          </div>
        )}
      </div>

      <DetailsModal open={detailsOpen} onClose={closeDetails} notification={selected} />
    </div>
  );
};

export default NotificationFeed;
